import { useState } from 'react';
import axios from 'redaxios';
import { apiConfig } from '../lib/apiConfig';
import { ParticleButton } from '@/components/ui/particle-button';
import useFileUploader from '../hooks/useFileUploader';

/**
 * The Checkout component now:
 * - Receives `data`, `address`, `userInfo`, etc. from Main
 * - Rewrites the CSV with "full-address" columns
 * - Calls `uploadToS3` from useFileUploader
 * - On success, calls the Stripe checkout
 */
export default function Checkout({
  isPurchaseDisabled,
  data,
  address,
  userInfo,
  setUploadedFileMeta,
  totalPrice,
  skipTracingService,
  zillowService,
  sessionToken,
}) {
  const [error, setError] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [isFileLoading, setFileLoading] = useState(false);
  const { uploadToS3, uploadError, uploadProgress, isUploading, uploadStats } =
    useFileUploader(sessionToken);

  async function handleClick() {
    try {
      if (isPurchaseDisabled || isLoading || isUploading) return;
      setError(null);
      setLoading(true);

      // 1) Build the new CSV with appended "full-address"
      const newData = data.map((row, index) => {
        if (index === 0) {
          return [...row, 'full-address'];
        } else {
          const streetVal = row[address.street.index] ?? '';
          const cityVal = row[address.city.index] ?? '';
          const stateVal = row[address.state.index] ?? '';
          const zipVal = row[address.zip.index] ?? '';
          const fullAddress = `${streetVal}, ${cityVal}, ${stateVal}, ${zipVal}`;
          return [...row, fullAddress];
        }
      });

      const csvHeaders = newData[0];
      const csvRows = newData.slice(1);
      const csvContent = [csvHeaders, ...csvRows]
        .map((arr) => arr.map((val) => `"${(val || '').toString().replace(/"/g, '""')}"`).join(','))
        .join('\n');

      const csvFile = new File([csvContent], 'converted.csv', {
        type: 'text/csv',
      });

      // 2) Upload the file to S3
      setFileLoading(true);
      const { statusCode, userId, filePattern } = await uploadToS3(csvFile, userInfo);
      setFileLoading(false);

      if (statusCode < 200 || statusCode >= 300) {
        throw new Error('S3 upload failed; please try again');
      }

      // If successful, store metadata
      if (setUploadedFileMeta) {
        setUploadedFileMeta({ userId, filePattern });
      }

      // 3) Call secure checkout endpoint
      try {
        // Check if we're in development mode
        const isDev =
          window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const enableRealCheckout = import.meta.env.VITE_ENABLE_REAL_CHECKOUT === 'true';

        if (isDev && !enableRealCheckout && !window.location.href.includes('vercel.app')) {
          // Development mode - show success message
          console.log('[DEV] Simulating Stripe checkout for local development');
          alert(
            `[DEV MODE] Checkout would process:\n\nUser: ${userInfo.email}\nTotal: $${totalPrice}\nFile: ${filePattern}\n\nIn production, this would redirect to Stripe.`
          );

          // Reset form or redirect to success page
          window.location.href = '/success?dev=true';
        } else {
          // Production mode - use real API
          // Send the row count, not a price — the server recomputes the
          // authoritative amount from it (data[0] is the header row).
          const rowCount = Math.max(0, data.length - 1);
          const checkoutResponse = await axios.post(
            '/api/create-checkout',
            {
              userId: userId,
              rowCount: rowCount,
              filename: filePattern,
              email: userInfo.email,
              skipTracing: skipTracingService,
              zillowService: zillowService,
              uploadedFileMeta: { userId, filePattern },
            },
            {
              headers: {
                Authorization: `Bearer ${sessionToken}`,
                'Content-Type': 'application/json',
              },
            }
          );

          if (checkoutResponse.data?.checkoutUrl) {
            window.location.href = checkoutResponse.data.checkoutUrl;
          } else {
            throw new Error('Invalid checkout response');
          }
        }
      } catch (checkoutError) {
        console.error('Checkout error:', checkoutError);
        setError(checkoutError);
        throw checkoutError;
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="checkout w-full flex flex-col items-center">
      {/* (Optional) We could show a small progress for the CSV rewriting or for the S3 upload */}
      {isUploading && (
        <div className="my-4 w-full max-w-xs">
          <div className="space-y-2">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Uploading... {Math.round(uploadProgress)}%</span>
              {uploadStats.speed && (
                <span className="text-gray-600">
                  {uploadStats.speed} • {uploadStats.eta} left
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <ParticleButton
          variant="default"
          disabled={isPurchaseDisabled || isLoading || isFileLoading || isUploading}
          onClick={handleClick}
          className="purchase-button"
          successDuration={1000}
        >
          {isLoading || isFileLoading || isUploading ? 'Processing...' : 'Purchase Now'}
        </ParticleButton>

        {error && (
          <div className="text-red-500 text-sm ml-2">
            {error.message || error.toString()}
            <button
              onClick={() => {
                setError(null);
                handleClick();
              }}
              className="ml-2 text-indigo-400 hover:text-indigo-300"
            >
              Retry
            </button>
          </div>
        )}
        {uploadError && !error && <div className="text-red-500 text-sm ml-2">{uploadError}</div>}
      </div>
    </div>
  );
}
